import fs from 'node:fs'
import path from 'node:path'

export interface NextConfigScaffoldOptions {
  cwd: string
  nextMajorVersion: number
}

export type NextConfigScaffoldStatus =
  | 'created'
  | 'updated'
  | 'already-set'
  | 'unsupported'
  | 'not-needed'

export interface NextConfigScaffoldResult {
  status: NextConfigScaffoldStatus
}

type CacheConfigMode = 'experimental-use-cache' | 'cache-components'

const BETTERSTART_ASSET_HOST = 'assets.betterstart.dev'

interface ContentUpdateResult {
  content: string
  changed: boolean
  unsupported?: boolean
}

/**
 * Ensure BetterStart's required Next.js config is present in the user's next.config.
 * Next.js 15 uses `experimental.useCache = true`.
 * Next.js 16+ uses top-level `cacheComponents = true`.
 * BetterStart remote assets also require an `images` allowance for `assets.betterstart.dev`.
 */
export function scaffoldNextConfig({
  cwd,
  nextMajorVersion
}: NextConfigScaffoldOptions): NextConfigScaffoldResult {
  const cacheConfigMode = resolveCacheConfigMode(nextMajorVersion)
  if (!cacheConfigMode) {
    return { status: 'not-needed' }
  }

  const variants = ['next.config.ts', 'next.config.mjs', 'next.config.js']
  let configPath: string | null = null

  for (const variant of variants) {
    const candidate = path.resolve(cwd, variant)
    if (fs.existsSync(candidate)) {
      configPath = candidate
      break
    }
  }

  if (!configPath) {
    const newPath = path.resolve(cwd, 'next.config.ts')
    fs.writeFileSync(newPath, createNextConfigTemplate(cacheConfigMode), 'utf-8')
    return { status: 'created' }
  }

  const content = fs.readFileSync(configPath, 'utf-8')
  const configObject = findConfigObject(content)

  if (!configObject) {
    return { status: 'unsupported' }
  }

  const cacheUpdateResult =
    cacheConfigMode === 'cache-components'
      ? ensureCacheComponents(content, configObject)
      : ensureExperimentalUseCache(content, configObject)

  if (cacheUpdateResult.unsupported) {
    return { status: 'unsupported' }
  }

  const updatedConfigObject = findConfigObject(cacheUpdateResult.content)
  if (!updatedConfigObject) {
    return { status: 'unsupported' }
  }

  const imagesUpdateResult = ensureBetterStartImageHost(
    cacheUpdateResult.content,
    updatedConfigObject
  )
  if (imagesUpdateResult.unsupported) {
    return { status: 'unsupported' }
  }

  const changed = cacheUpdateResult.changed || imagesUpdateResult.changed
  if (!changed) {
    return { status: 'already-set' }
  }

  fs.writeFileSync(configPath, imagesUpdateResult.content, 'utf-8')
  return { status: 'updated' }
}

function resolveCacheConfigMode(nextMajorVersion: number): CacheConfigMode | null {
  if (nextMajorVersion >= 16) return 'cache-components'
  if (nextMajorVersion >= 15) return 'experimental-use-cache'
  return null
}

function createNextConfigTemplate(mode: CacheConfigMode): string {
  if (mode === 'cache-components') {
    return `import type { NextConfig } from 'next'

const nextConfig: NextConfig = {
  cacheComponents: true,
  devIndicators: false,
  images: {
    remotePatterns: [
      {
        protocol: 'https',
        hostname: '${BETTERSTART_ASSET_HOST}',
      },
    ],
  },
}

export default nextConfig
`
  }

  return `import type { NextConfig } from 'next'

const nextConfig: NextConfig = {
  experimental: {
    useCache: true,
  },
  images: {
    remotePatterns: [
      {
        protocol: 'https',
        hostname: '${BETTERSTART_ASSET_HOST}',
      },
    ],
  },
}

export default nextConfig
`
}

function ensureCacheComponents(
  content: string,
  configObject: ConfigObjectMatch
): ContentUpdateResult {
  return upsertTopLevelBooleanProperty(content, configObject, 'cacheComponents')
}

function ensureExperimentalUseCache(
  content: string,
  configObject: ConfigObjectMatch
): ContentUpdateResult {
  let updatedContent = content
  let changed = false

  const withoutCacheComponents = removeTopLevelProperty(
    updatedContent,
    configObject,
    'cacheComponents'
  )
  updatedContent = withoutCacheComponents.content
  changed = withoutCacheComponents.changed

  const refreshedConfigObject = findConfigObject(updatedContent)
  if (!refreshedConfigObject) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalProp = findTopLevelProperty(
    updatedContent,
    refreshedConfigObject.start,
    refreshedConfigObject.end,
    'experimental'
  )

  if (!experimentalProp) {
    return {
      content:
        updatedContent.slice(0, refreshedConfigObject.start + 1) +
        `\n${refreshedConfigObject.propertyIndent}experimental: {\n${refreshedConfigObject.propertyIndent}  useCache: true,\n${refreshedConfigObject.propertyIndent}},` +
        updatedContent.slice(refreshedConfigObject.start + 1),
      changed: true
    }
  }

  const experimentalValue = experimentalProp.value.trim()
  if (!experimentalValue.startsWith('{')) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalObjectStart = updatedContent.indexOf('{', experimentalProp.valueStart)
  if (experimentalObjectStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalObjectEnd = findMatchingBrace(updatedContent, experimentalObjectStart)
  if (experimentalObjectEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  const useCacheProp = findTopLevelProperty(
    updatedContent,
    experimentalObjectStart,
    experimentalObjectEnd,
    'useCache'
  )

  if (useCacheProp) {
    if (useCacheProp.value.trim() === 'true') {
      return { content: updatedContent, changed }
    }

    return {
      content:
        updatedContent.slice(0, useCacheProp.valueStart) +
        'true' +
        updatedContent.slice(useCacheProp.valueEnd),
      changed: true
    }
  }

  const propertyIndent = detectPropertyIndent(updatedContent, experimentalObjectStart)
  return {
    content:
      updatedContent.slice(0, experimentalObjectStart + 1) +
      `\n${propertyIndent}useCache: true,` +
      updatedContent.slice(experimentalObjectStart + 1),
    changed: true
  }
}

function ensureBetterStartImageHost(
  content: string,
  configObject: ConfigObjectMatch
): ContentUpdateResult {
  const imagesProp = findTopLevelProperty(content, configObject.start, configObject.end, 'images')

  if (!imagesProp) {
    return {
      content:
        content.slice(0, configObject.start + 1) +
        `\n${configObject.propertyIndent}images: {\n${configObject.propertyIndent}  remotePatterns: [\n${configObject.propertyIndent}    {\n${configObject.propertyIndent}      protocol: 'https',\n${configObject.propertyIndent}      hostname: '${BETTERSTART_ASSET_HOST}',\n${configObject.propertyIndent}    },\n${configObject.propertyIndent}  ],\n${configObject.propertyIndent}},` +
        content.slice(configObject.start + 1),
      changed: true
    }
  }

  const imagesValue = imagesProp.value.trim()
  if (!imagesValue.startsWith('{')) {
    return { content, changed: false, unsupported: true }
  }

  const imagesObjectStart = content.indexOf('{', imagesProp.valueStart)
  if (imagesObjectStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const imagesObjectEnd = findMatchingBrace(content, imagesObjectStart)
  if (imagesObjectEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  if (hasBetterStartImageHostConfigured(content, imagesObjectStart, imagesObjectEnd)) {
    return { content, changed: false }
  }

  const remotePatternsProp = findTopLevelProperty(
    content,
    imagesObjectStart,
    imagesObjectEnd,
    'remotePatterns'
  )
  if (!remotePatternsProp) {
    const propertyIndent = detectPropertyIndent(content, imagesObjectStart)
    return {
      content:
        content.slice(0, imagesObjectStart + 1) +
        `\n${propertyIndent}remotePatterns: [\n${propertyIndent}  {\n${propertyIndent}    protocol: 'https',\n${propertyIndent}    hostname: '${BETTERSTART_ASSET_HOST}',\n${propertyIndent}  },\n${propertyIndent}],` +
        content.slice(imagesObjectStart + 1),
      changed: true
    }
  }

  const remotePatternsValue = remotePatternsProp.value.trim()
  if (!remotePatternsValue.startsWith('[')) {
    return { content, changed: false, unsupported: true }
  }

  const arrayStart = content.indexOf('[', remotePatternsProp.valueStart)
  if (arrayStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const arrayEnd = findMatchingBracket(content, arrayStart)
  if (arrayEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  if (content.slice(arrayStart, arrayEnd + 1).includes(BETTERSTART_ASSET_HOST)) {
    return { content, changed: false }
  }

  const arrayIndent = detectPropertyIndent(content, arrayStart)
  const closingIndent = getLineIndent(content, arrayStart)
  const beforeClose = content.slice(arrayStart + 1, arrayEnd)
  const trailingWhitespace = beforeClose.match(/\s*$/)?.[0] ?? ''
  const insertionPoint = arrayEnd - trailingWhitespace.length
  const normalizedItems = beforeClose.slice(0, beforeClose.length - trailingWhitespace.length)
  const separator = normalizedItems.trim().length === 0 || /,\s*$/.test(normalizedItems) ? '' : ','
  const closingGap = trailingWhitespace.length > 0 ? trailingWhitespace : `\n${closingIndent}`

  return {
    content:
      content.slice(0, insertionPoint) +
      `${separator}\n${arrayIndent}{\n${arrayIndent}  protocol: 'https',\n${arrayIndent}  hostname: '${BETTERSTART_ASSET_HOST}',\n${arrayIndent}},${closingGap}` +
      content.slice(arrayEnd),
    changed: true
  }
}

function upsertTopLevelBooleanProperty(
  content: string,
  configObject: ConfigObjectMatch,
  propertyName: string
): ContentUpdateResult {
  const existingProp = findTopLevelProperty(
    content,
    configObject.start,
    configObject.end,
    propertyName
  )

  if (existingProp) {
    if (existingProp.value.trim() === 'true') {
      return { content, changed: false }
    }

    return {
      content: `${content.slice(0, existingProp.valueStart)}true${content.slice(existingProp.valueEnd)}`,
      changed: true
    }
  }

  return {
    content:
      content.slice(0, configObject.start + 1) +
      `\n${configObject.propertyIndent}${propertyName}: true,` +
      content.slice(configObject.start + 1),
    changed: true
  }
}

function removeTopLevelProperty(
  content: string,
  configObject: ConfigObjectMatch,
  propertyName: string
): ContentUpdateResult {
  const property = findTopLevelProperty(content, configObject.start, configObject.end, propertyName)
  if (!property) {
    return { content, changed: false }
  }

  const propertyLineStart = content.lastIndexOf('\n', property.propertyStart) + 1
  let propertyEnd = property.valueEnd

  if (content[propertyEnd] === ',') propertyEnd++
  if (content[propertyEnd] === '\r') propertyEnd++
  if (content[propertyEnd] === '\n') propertyEnd++

  return {
    content: content.slice(0, propertyLineStart) + content.slice(propertyEnd),
    changed: true
  }
}

interface ConfigObjectMatch {
  start: number
  end: number
  propertyIndent: string
}

interface PropertyMatch {
  propertyStart: number
  value: string
  valueStart: number
  valueEnd: number
}

interface CodeScannerState {
  inString: '"' | "'" | '`' | null
  inLineComment: boolean
  inBlockComment: boolean
}

type TopLevelScanResult =
  | { skip: true; nextIndex: number; depth: number }
  | { skip: false; nextIndex: number; depth: number; char: string }

const CONFIG_OBJECT_PATTERNS = [
  /\b(?:const|let|var)\s+\w+\s*:\s*NextConfig\s*=\s*\{/g,
  /\b(?:const|let|var)\s+nextConfig\s*=\s*\{/g,
  /\b(?:const|let|var)\s+\w+\s*:\s*NextConfig\s*=\s*(?:[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\(\s*)+\{/g,
  /\b(?:const|let|var)\s+nextConfig\s*=\s*(?:[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\(\s*)+\{/g,
  /export\s+default\s+\{/g,
  /export\s+default\s+(?:[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\(\s*)+\{/g,
  /module\.exports\s*=\s*\{/g,
  /module\.exports\s*=\s*(?:[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\(\s*)+\{/g
]

function findConfigObject(content: string): ConfigObjectMatch | null {
  let openingBrace = -1
  let earliestMatch = Number.POSITIVE_INFINITY

  for (const pattern of CONFIG_OBJECT_PATTERNS) {
    const match = pattern.exec(content)
    pattern.lastIndex = 0
    if (!match) {
      continue
    }

    if (match.index >= earliestMatch) {
      continue
    }

    earliestMatch = match.index
    openingBrace = match.index + match[0].lastIndexOf('{')
  }

  if (openingBrace < 0) {
    return null
  }

  const closingBrace = findMatchingBrace(content, openingBrace)
  if (closingBrace < 0) {
    return null
  }

  return {
    start: openingBrace,
    end: closingBrace,
    propertyIndent: detectPropertyIndent(content, openingBrace)
  }
}

function detectPropertyIndent(content: string, objectStart: number): string {
  const lineStart = content.lastIndexOf('\n', objectStart) + 1
  const baseIndent = content.slice(lineStart, objectStart).match(/^\s*/)?.[0] ?? ''
  const body = content.slice(objectStart + 1)
  const propIndent = body.match(/\n([ \t]+)\S/)

  return propIndent?.[1] ?? `${baseIndent}  `
}

function findMatchingBrace(content: string, openingBrace: number): number {
  return findMatchingDelimiter(content, openingBrace, '{', '}')
}

function findMatchingBracket(content: string, openingBracket: number): number {
  return findMatchingDelimiter(content, openingBracket, '[', ']')
}

function findMatchingDelimiter(
  content: string,
  openingIndex: number,
  openingDelimiter: string,
  closingDelimiter: string
): number {
  let depth = 0
  const scannerState = createCodeScannerState()

  for (let i = openingIndex; i < content.length; i++) {
    const scanResult = advanceCodeScanner(content, i, scannerState)
    if (scanResult.skip) {
      i = scanResult.nextIndex
      continue
    }

    const char = content[i]

    if (char === openingDelimiter) {
      depth++
      continue
    }

    if (char === closingDelimiter) {
      depth--
      if (depth === 0) return i
    }
  }

  return -1
}

function createCodeScannerState(): CodeScannerState {
  return {
    inString: null,
    inLineComment: false,
    inBlockComment: false
  }
}

function getLineIndent(content: string, position: number): string {
  const lineStart = content.lastIndexOf('\n', position) + 1
  return content.slice(lineStart, position).match(/^\s*/)?.[0] ?? ''
}

function hasBetterStartImageHostConfigured(
  content: string,
  imagesObjectStart: number,
  imagesObjectEnd: number
): boolean {
  const domainsProp = findTopLevelProperty(content, imagesObjectStart, imagesObjectEnd, 'domains')
  if (domainsProp?.value.includes(BETTERSTART_ASSET_HOST)) {
    return true
  }

  const remotePatternsProp = findTopLevelProperty(
    content,
    imagesObjectStart,
    imagesObjectEnd,
    'remotePatterns'
  )
  return remotePatternsProp?.value.includes(BETTERSTART_ASSET_HOST) ?? false
}

function findTopLevelProperty(
  content: string,
  objectStart: number,
  objectEnd: number,
  propertyName: string
): PropertyMatch | null {
  let depth = 0
  const scannerState = createCodeScannerState()

  for (let i = objectStart + 1; i < objectEnd; i++) {
    const scanResult = advanceTopLevelScanner(content, i, scannerState, depth)
    depth = scanResult.depth
    if (scanResult.skip) {
      i = scanResult.nextIndex
      continue
    }

    if (!isIdentifierStart(scanResult.char)) {
      continue
    }

    if (
      !content.startsWith(propertyName, i) ||
      !isIdentifierBoundary(content[i + propertyName.length])
    ) {
      continue
    }

    let cursor = i + propertyName.length
    while (cursor < objectEnd && /\s/.test(content[cursor]!)) cursor++
    if (content[cursor] !== ':') continue

    cursor++
    while (cursor < objectEnd && /\s/.test(content[cursor]!)) cursor++
    const valueStart = cursor
    const valueEnd = findTopLevelValueEnd(content, valueStart, objectEnd)

    return {
      propertyStart: i,
      value: content.slice(valueStart, valueEnd),
      valueStart,
      valueEnd
    }
  }

  return null
}

function findTopLevelValueEnd(content: string, valueStart: number, objectEnd: number): number {
  let depth = 0
  const scannerState = createCodeScannerState()

  for (let i = valueStart; i < objectEnd; i++) {
    const scanResult = advanceTopLevelScanner(content, i, scannerState, depth)
    depth = scanResult.depth
    if (scanResult.skip) {
      i = scanResult.nextIndex
      continue
    }

    if (scanResult.char === ',') {
      return i
    }
  }

  return objectEnd
}

function advanceTopLevelScanner(
  content: string,
  index: number,
  state: CodeScannerState,
  depth: number
): TopLevelScanResult {
  const scanResult = advanceCodeScanner(content, index, state)
  if (scanResult.skip) {
    return { ...scanResult, depth }
  }

  const char = content[index]!

  if (char === '{' || char === '[' || char === '(') {
    return { skip: true, nextIndex: index, depth: depth + 1 }
  }

  if (char === '}' || char === ']' || char === ')') {
    return { skip: true, nextIndex: index, depth: depth - 1 }
  }

  if (depth !== 0) {
    return { skip: true, nextIndex: index, depth }
  }

  return { skip: false, nextIndex: index, depth, char }
}

function advanceCodeScanner(
  content: string,
  index: number,
  state: CodeScannerState
): { skip: true; nextIndex: number } | { skip: false; nextIndex: number } {
  const char = content[index]
  const next = content[index + 1]
  const prev = content[index - 1]

  if (state.inLineComment) {
    if (char === '\n') state.inLineComment = false
    return { skip: true, nextIndex: index }
  }

  if (state.inBlockComment) {
    if (prev === '*' && char === '/') state.inBlockComment = false
    return { skip: true, nextIndex: index }
  }

  if (state.inString) {
    if (char === state.inString && prev !== '\\') state.inString = null
    return { skip: true, nextIndex: index }
  }

  if (char === '/' && next === '/') {
    state.inLineComment = true
    return { skip: true, nextIndex: index + 1 }
  }

  if (char === '/' && next === '*') {
    state.inBlockComment = true
    return { skip: true, nextIndex: index + 1 }
  }

  if (char === '"' || char === "'" || char === '`') {
    state.inString = char
    return { skip: true, nextIndex: index }
  }

  return { skip: false, nextIndex: index }
}

function isIdentifierStart(char: string | undefined): boolean {
  return !!char && /[A-Za-z_$]/.test(char)
}

function isIdentifierBoundary(char: string | undefined): boolean {
  return !char || !/[A-Za-z0-9_$]/.test(char)
}
